import { useState, useEffect, useCallback } from 'react';
import { EmailSenderIdentity } from '../../../services/email/types';
import { staffService } from '../../../services/staff/StaffService';

interface UseSenderManagementResult {
  senders: EmailSenderIdentity[];
  selectedSender: EmailSenderIdentity | undefined;
  setSelectedSender: (sender: EmailSenderIdentity) => void;
  loading: boolean;
  error: string | null;
  photoValidationErrors: Record<string, string>;
  // true, wenn der Absender NICHT über die E-Mail des eingeloggten Users
  // aufgelöst werden konnte und deshalb still auf den Primär-Mitarbeiter
  // (bzw. den ersten Eintrag) zurueckgefallen ist. Signalisiert der UI, dass
  // die Vorbelegung unsicher ist (z. B. abgelaufene Sitzung) und der Absender
  // geprüft werden sollte. Wird false, sobald der User bewusst einen Absender
  // auswählt.
  senderIdentityUnconfirmed: boolean;
}

/**
 * Hook für sauberes Sender-Management mit Smart-Matching
 * Folgt React Best Practices ohne ESLint-Hacks
 *
 * requirePhoto (Default true): Bestandsverhalten — nur Mitarbeiter mit gültigem
 * Foto sind wählbar (Finalisieren/UploadModal/ReleaseModal, Foto-Signatur ist
 * dort Pflicht). Mit requirePhoto=false entfällt die Foto-Validierung komplett
 * (Ticketsystem: die Ticket-Signatur hat einen Text-Fallback ohne Foto, dort
 * dürfen auch Mitarbeiter ohne Foto als Absender gewählt werden).
 */
export const useSenderManagement = (
  isOpen: boolean,
  userEmail?: string,
  requirePhoto: boolean = true
): UseSenderManagementResult => {
  const [senders, setSenders] = useState<EmailSenderIdentity[]>([]);
  const [selectedSender, setSelectedSenderState] = useState<EmailSenderIdentity | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoValidationErrors, setPhotoValidationErrors] = useState<Record<string, string>>({});
  // Markierung, ob die automatische Absender-Vorbelegung ueber die E-Mail des
  // eingeloggten Users bestätigt wurde. Startwert false; wird im Smart-Matching
  // gesetzt und bei bewusster Auswahl bzw. Modal-Schließung zurueckgesetzt.
  const [senderIdentityUnconfirmed, setSenderIdentityUnconfirmed] = useState(false);

  // Sender laden - saubere useCallback ohne problematische Dependencies
  const loadSenders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setPhotoValidationErrors({});
      
      // Lade XTAX-Mitarbeiter aus der Datenbank
      const staffMembers = await staffService.getActiveStaff();
      
      if (staffMembers.length === 0) {
        throw new Error('Keine aktiven Mitarbeiter gefunden');
      }

      // Ohne Foto-Pflicht (requirePhoto=false): alle aktiven Mitarbeiter sind
      // wählbar, keine Foto-Validierung. Das finally übernimmt setLoading(false).
      if (!requirePhoto) {
        setSenders(staffMembers);
        return;
      }

      // Validiere Fotos für alle Mitarbeiter und filtere nur die mit gültigen Fotos
      const validSenders: EmailSenderIdentity[] = [];
      const validationErrors: Record<string, string> = {};
      
      for (const member of staffMembers) {
        const validation = await staffService.validateSenderPhoto(member.id);
        
        if (validation.valid) {
          validSenders.push(member);
        } else {
          validationErrors[member.id] = validation.error || 'Foto-Problem';
        }
      }

      // Nur Mitarbeiter mit gültigen Fotos werden angezeigt
      if (validSenders.length === 0) {
        throw new Error('Keine Mitarbeiter mit gültigen Fotos gefunden');
      }

      setPhotoValidationErrors(validationErrors);
      setSenders(validSenders);
      
    } catch (err) {
      console.error('Fehler beim Laden der Absender:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Absender');
    
      // Fallback: Nur Tim Wolter (Default Sender) - aber nur wenn er ein gültiges Foto hat
      const fallbackSender = staffService.getDefaultSender();

      // Ohne Foto-Pflicht entfällt auch die Fallback-Foto-Validierung.
      if (!requirePhoto) {
        setSenders([fallbackSender]);
        return;
      }

      const validation = await staffService.validateSenderPhoto(fallbackSender.id);
      
      if (validation.valid) {
        setSenders([fallbackSender]);
      } else {
        setSenders([]);
        setError('Keine Mitarbeiter mit gültigen Fotos verfügbar');
      }
    } finally {
      setLoading(false);
    }
  }, [requirePhoto]);

  // Smart-Matching als separater Effect - saubere Trennung
  //
  // Neu-Matching-Bedingung (Fix): läuft nicht nur einmalig bei leerer Auswahl,
  // sondern erneut, solange die bisherige Vorbelegung ein unbestätigter
  // Fallback war (senderIdentityUnconfirmed === true). Damit wird eine
  // verspätet eintreffende userEmail (z. B. Token-Rekonstruktion nach dem
  // Laden der Sender) noch korrekt gematcht und ein Fehlalarm vermieden. Eine
  // bewusst bestätigte Auswahl bzw. eine bereits über die eingeloggte
  // User-E-Mail bestätigte Vorbelegung (jeweils senderIdentityUnconfirmed ===
  // false) bleibt unangetastet.
  useEffect(() => {
    if (senders.length > 0 && (!selectedSender || senderIdentityUnconfirmed)) {
      let defaultSender: EmailSenderIdentity;
      // Nur eine über die eingeloggte User-E-Mail aufgelöste Vorbelegung gilt
      // als bestätigt. Jeder isPrimary-/senders[0]-Fallback bleibt unsicher.
      let matchedLoggedInUser = false;

      // 1. Priorität: Sender mit derselben E-Mail wie eingeloggter User
      if (userEmail) {
        // Mehrere Matching-Versuche für robuste Erkennung
        let matchingSender = senders.find(sender => sender.email === userEmail);
        
        if (!matchingSender) {
          // Fallback: Case-insensitive Vergleich
          matchingSender = senders.find(sender => 
            sender.email?.toLowerCase() === userEmail.toLowerCase()
          );
        }
        
        if (!matchingSender) {
          // Fallback: Trimmed Vergleich
          matchingSender = senders.find(sender => 
            sender.email?.trim() === userEmail.trim()
          );
        }
        
        if (matchingSender) {
          defaultSender = matchingSender;
          matchedLoggedInUser = true;
        } else {
          // 2. Priorität: Tim Wolter (primärer Mitarbeiter) als Fallback
          defaultSender = senders.find(member => member.isPrimary) || senders[0];
        }
      } else {
        // 3. Priorität: Tim Wolter (primärer Mitarbeiter) wenn keine User-E-Mail vorhanden
        defaultSender = senders.find(member => member.isPrimary) || senders[0];
      }

      setSelectedSenderState(defaultSender);
      setSenderIdentityUnconfirmed(!matchedLoggedInUser);
    }
  }, [senders, selectedSender, senderIdentityUnconfirmed, userEmail]);

  // Bewusste Auswahl durch den User bestätigt die Absender-Identität und hebt
  // eine ggf. gesetzte Unsicherheits-Markierung auf. Nach außen ersetzt dieser
  // Wrapper den rohen State-Setter (gleiche Signatur, kein Umbau bei Aufrufern).
  const setSelectedSender = useCallback((sender: EmailSenderIdentity) => {
    setSelectedSenderState(sender);
    setSenderIdentityUnconfirmed(false);
  }, []);

  // Laden nur wenn Modal offen ist
  useEffect(() => {
    if (isOpen && senders.length === 0) {
      loadSenders();
    }
  }, [isOpen, loadSenders, senders.length]);

  // Reset bei Modal-Schließung
  useEffect(() => {
    if (!isOpen) {
      setSelectedSenderState(undefined);
      setSenders([]);
      setLoading(false);
      setError(null);
      setPhotoValidationErrors({});
      setSenderIdentityUnconfirmed(false);
    }
  }, [isOpen]);

  return {
    senders,
    selectedSender,
    setSelectedSender,
    loading,
    error,
    photoValidationErrors,
    senderIdentityUnconfirmed
  };
};
